import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Container,
  Typography,
  Grid,
  Stack,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  TableContainer,
  Card,
  CardContent,
  LinearProgress,
  Snackbar,
  Checkbox
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ShieldIcon from '@mui/icons-material/Shield';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewListIcon from '@mui/icons-material/ViewList';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { HeaderCard, SectionPaper } from './accounting-overview-components/layout';
import AutoCollectionsDialog from './accounting-receivables-components/AutoCollectionsDialog';
import ReceivablesSummary from './accounting-receivables-components/ReceivablesSummary';
import ReceivablesCalendar from './accounting-receivables-components/ReceivablesCalendar';

const filters = ['All', 'Pending', 'Overdue', 'Paid', 'Partial'];

const AccountingReceivablesPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    canViewAccountingReceivables,
    canEditAccountingReceivables,
    canDeleteAccountingReceivables
  } = usePermissions();
  
  // All hooks must be at the top before any conditional returns
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dueInDays, setDueInDays] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [metrics, setMetrics] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    dso: 0,
    highRisk: 0
  });
  const [summaryStats, setSummaryStats] = useState({
    totalOutstanding: 0,
    unpaidCount: 0,
    overdueAmount: 0,
    overdueCount: 0,
    dueNext30Days: 0,
    dueNext30DaysCount: 0,
    totalPaid: 0,
    paidCount: 0
  });
  const [workflowMenuAnchor, setWorkflowMenuAnchor] = useState(null);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [autoCollectionsOpen, setAutoCollectionsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedReceivables, setSelectedReceivables] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'

  const fetchCompanyData = async () => {
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Fetch receivables data
  useEffect(() => {
    if (companyId) {
      fetchReceivables();
      fetchCompanyData();
    }
  }, [companyId]);

  const fetchReceivables = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch regular invoices only
      const { data: invoices, error: invError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total_amount,
          status,
          due_date,
          invoice_date,
          created_at,
          customer_id,
          customer:customer_id(name, company),
          payment_terms,
          purchase_order_number
        `)
        .eq('company_id', companyId)
        .in('status', ['pending', 'overdue', 'paid', 'partial'])
        .order('created_at', { ascending: false });

      if (invError) throw invError;

      // Also fetch related proforma invoice IDs if purchase_order_number exists
      const invoicesWithPO = (invoices || []).filter(inv => inv.purchase_order_number);
      const poNumbers = invoicesWithPO.map(inv => inv.purchase_order_number);
      
      let proformaInvoiceMap = {};
      if (poNumbers.length > 0) {
        const { data: proformaInvoices, error: piError } = await supabase
          .from('customer_purchase_orders')
          .select('id, po_number')
          .eq('company_id', companyId)
          .in('po_number', poNumbers);

        if (!piError && proformaInvoices) {
          proformaInvoices.forEach(pi => {
            proformaInvoiceMap[pi.po_number] = pi.id;
          });
        }
      }

      // Transform regular invoices to receivables format
      const invReceivables = (invoices || []).map(inv => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setMinutes(0, 0, 0);
        today.setSeconds(0, 0);
        today.setMilliseconds(0);
        
        const dueDate = inv.due_date ? new Date(inv.due_date) : null;
        let daysOutstanding = 0;
        let dueDateOnly = null;
        
        if (dueDate) {
          dueDateOnly = new Date(dueDate);
          dueDateOnly.setHours(0, 0, 0, 0);
          dueDateOnly.setMinutes(0, 0, 0);
          dueDateOnly.setSeconds(0, 0);
          dueDateOnly.setMilliseconds(0);
          
          // Calculate days outstanding: (today - due_date) in days
          // If today > due_date, result is positive (overdue)
          // If today < due_date, result is negative (not yet due)
          const diffTime = today.getTime() - dueDateOnly.getTime();
          daysOutstanding = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        }

        // Determine status
        let status = inv.status;
        if (status === 'pending' && dueDateOnly && daysOutstanding > 0) {
          status = 'overdue';
        }

        return {
          id: inv.id,
          type: 'invoice',
          documentNumber: inv.invoice_number,
          customer: inv.customer?.name || inv.customer?.company || 'Unknown Customer',
          amount: parseFloat(inv.total_amount || 0),
          status: status.charAt(0).toUpperCase() + status.slice(1), // Capitalize first letter
          dueDate: inv.due_date || null,
          daysOutstanding: daysOutstanding,
          orderDate: inv.invoice_date,
          purchaseOrderNumber: inv.purchase_order_number,
          proformaInvoiceId: inv.purchase_order_number ? proformaInvoiceMap[inv.purchase_order_number] : null
        };
      });

      // Sort by invoice date (newest first - descending order)
      const allReceivables = invReceivables.sort((a, b) => {
        const dateA = new Date(a.orderDate || 0);
        const dateB = new Date(b.orderDate || 0);
        return dateB - dateA; // Descending order (newest first)
      });

      setReceivables(allReceivables);

      // Calculate summary stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      today.setMinutes(0, 0, 0);
      today.setSeconds(0, 0);
      today.setMilliseconds(0);
      
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const stats = {
        totalOutstanding: 0,
        unpaidCount: 0,
        overdueAmount: 0,
        overdueCount: 0,
        dueNext30Days: 0,
        dueNext30DaysCount: 0,
        totalPaid: 0,
        paidCount: 0
      };

      allReceivables.forEach(receivable => {
        const status = receivable.status.toLowerCase();
        
        if (status === 'paid') {
          stats.totalPaid += receivable.amount;
          stats.paidCount++;
        } else {
          // Unpaid invoices (pending, overdue, partial)
          stats.totalOutstanding += receivable.amount;
          stats.unpaidCount++;

          // Check if overdue
          if (receivable.dueDate) {
            const dueDate = new Date(receivable.dueDate);
            dueDate.setHours(0, 0, 0, 0);
            dueDate.setMinutes(0, 0, 0);
            dueDate.setSeconds(0, 0);
            dueDate.setMilliseconds(0);
            
            if (dueDate < today) {
              stats.overdueAmount += receivable.amount;
              stats.overdueCount++;
            } else if (dueDate <= thirtyDaysFromNow) {
              stats.dueNext30Days += receivable.amount;
              stats.dueNext30DaysCount++;
            }
          }
        }
      });

      setSummaryStats(stats);

      // Calculate metrics
      const totalInvoices = allReceivables.length;
      const totalAmount = allReceivables.reduce((sum, r) => sum + r.amount, 0);
      const overdueCount = allReceivables.filter(r => r.status.toLowerCase() === 'overdue').length;
      const avgDaysOutstanding = allReceivables.length > 0
        ? Math.round(allReceivables.reduce((sum, r) => sum + r.daysOutstanding, 0) / allReceivables.length)
        : 0;

      setMetrics({
        totalInvoices,
        totalAmount,
        dso: avgDaysOutstanding,
        highRisk: overdueCount
      });
    } catch (err) {
      console.error('Error fetching receivables:', err);
      setError('Failed to load receivables. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = receivables.filter((row) => {
    const matchesStatus = statusFilter === 'All' || 
      row.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesSearch = 
      row.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.documentNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by due in X days
    let matchesDueInDays = true;
    if (dueInDays !== '' && dueInDays !== null) {
      const days = parseInt(dueInDays);
      if (!isNaN(days) && row.dueDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setMinutes(0, 0, 0);
        today.setSeconds(0, 0);
        today.setMilliseconds(0);
        
        const dueDate = new Date(row.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        dueDate.setMinutes(0, 0, 0);
        dueDate.setSeconds(0, 0);
        dueDate.setMilliseconds(0);
        
        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        matchesDueInDays = daysUntilDue <= days && daysUntilDue >= 0;
      } else {
        matchesDueInDays = false;
      }
    }
    
    // Filter by date range (invoice date)
    let matchesDateRange = true;
    if (fromDate || toDate) {
      if (row.orderDate) {
        const invoiceDate = new Date(row.orderDate);
        invoiceDate.setHours(0, 0, 0, 0);
        invoiceDate.setMinutes(0, 0, 0);
        invoiceDate.setSeconds(0, 0);
        invoiceDate.setMilliseconds(0);
        
        if (fromDate) {
          const from = dayjs(fromDate).toDate();
          from.setHours(0, 0, 0, 0);
          from.setMinutes(0, 0, 0);
          from.setSeconds(0, 0);
          from.setMilliseconds(0);
          if (invoiceDate < from) {
            matchesDateRange = false;
          }
        }
        
        if (toDate && matchesDateRange) {
          const to = dayjs(toDate).toDate();
          to.setHours(23, 59, 59, 999);
          to.setMinutes(59, 59);
          to.setSeconds(59, 59);
          to.setMilliseconds(999);
          if (invoiceDate > to) {
            matchesDateRange = false;
          }
        }
      } else {
        matchesDateRange = false;
      }
    }
    
    return matchesStatus && matchesSearch && matchesDueInDays && matchesDateRange;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleViewWorkflowClick = (event, receivable) => {
    event.stopPropagation();
    setSelectedReceivable(receivable);
    setWorkflowMenuAnchor(event.currentTarget);
  };

  const handleWorkflowMenuClose = () => {
    setWorkflowMenuAnchor(null);
    setSelectedReceivable(null);
  };

  const handleViewOrderWorkflow = () => {
    if (selectedReceivable?.proformaInvoiceId) {
      window.open(`/company/${companyId}/proforma-invoices/${selectedReceivable.proformaInvoiceId}`, '_blank');
    }
    handleWorkflowMenuClose();
  };

  const handleViewInvoiceWorkflow = () => {
    if (!canEditAccountingReceivables()) {
      setError('You do not have permission to edit invoices.');
      handleWorkflowMenuClose();
      return;
    }
    if (selectedReceivable) {
      window.open(`/company/${companyId}/invoices/edit/${selectedReceivable.id}`, '_blank');
    }
    handleWorkflowMenuClose();
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReceivable || !canEditAccountingReceivables()) {
      setError('You do not have permission to update invoice status.');
      handleWorkflowMenuClose();
      return;
    }

    try {
      const { error } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .eq('id', selectedReceivable.id);

      if (error) throw error;

      // Refresh receivables data
      await fetchReceivables();
      
      // Show success message
      const statusLabels = {
        'paid': 'Paid',
        'pending': 'Pending',
        'overdue': 'Overdue',
        'partial': 'Partial'
      };
      setSnackbar({
        open: true,
        message: `Invoice marked as ${statusLabels[newStatus] || newStatus} successfully`,
        severity: 'success'
      });
      
      handleWorkflowMenuClose();
    } catch (err) {
      console.error('Error updating invoice status:', err);
      setError(`Failed to update invoice status: ${err.message}`);
      setSnackbar({
        open: true,
        message: `Failed to update invoice status: ${err.message}`,
        severity: 'error'
      });
      handleWorkflowMenuClose();
    }
  };

  const handleMarkAsPaid = () => {
    handleUpdateStatus('paid');
  };

  const handleMarkAsPending = () => {
    handleUpdateStatus('pending');
  };

  const handleMarkAsUnpaid = () => {
    // Marking as unpaid means setting back to pending
    handleUpdateStatus('pending');
  };

  // Bulk selection handlers
  const handleSelectReceivable = (receivableId) => {
    setSelectedReceivables(prev => {
      if (prev.includes(receivableId)) {
        return prev.filter(id => id !== receivableId);
      } else {
        return [...prev, receivableId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedReceivables(filteredRows.map(row => row.id));
    } else {
      setSelectedReceivables([]);
    }
  };

  const isAllSelected = selectedReceivables.length > 0 && selectedReceivables.length === filteredRows.length;
  const isIndeterminate = selectedReceivables.length > 0 && selectedReceivables.length < filteredRows.length;

  // Bulk status update handlers
  const handleBulkUpdateStatus = async (newStatus) => {
    if (selectedReceivables.length === 0 || !canEditAccountingReceivables()) {
      setError('You do not have permission to update invoice status or no items selected.');
      return;
    }

    try {
      const { error } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .in('id', selectedReceivables);

      if (error) throw error;

      // Refresh receivables data
      await fetchReceivables();
      
      // Clear selection
      setSelectedReceivables([]);
      
      // Show success message
      const statusLabels = {
        'paid': 'Paid',
        'pending': 'Pending',
        'overdue': 'Overdue',
        'partial': 'Partial'
      };
      setSnackbar({
        open: true,
        message: `${selectedReceivables.length} invoice(s) marked as ${statusLabels[newStatus] || newStatus} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating invoice statuses:', err);
      setSnackbar({
        open: true,
        message: `Failed to update invoice statuses: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleBulkMarkAsPaid = () => {
    handleBulkUpdateStatus('paid');
  };

  const handleBulkMarkAsUnpaid = () => {
    handleBulkUpdateStatus('pending');
  };

  const handleExportCSV = () => {
    try {
      // Create CSV headers
      let csvContent = "Invoice Number,Customer,Amount,Status,Due Date,Days Outstanding\n";
      
      // Add rows for filtered receivables
      filteredRows.forEach(row => {
        const invoiceNumber = `"${row.documentNumber}"`;
        const customer = `"${row.customer.replace(/"/g, '""')}"`;
        const amount = formatCurrency(row.amount).replace('$', '').replace(',', '');
        const status = `"${row.status}"`;
        const dueDate = row.dueDate ? formatDate(row.dueDate) : 'N/A';
        const daysOutstanding = row.daysOutstanding > 0 ? `+${row.daysOutstanding}` : row.daysOutstanding;
        
        csvContent += `${invoiceNumber},${customer},${amount},${status},"${dueDate}",${daysOutstanding}\n`;
      });
      
      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      // Generate filename with current date
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      link.setAttribute('download', `receivables_${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting receivables:', err);
      setError('Failed to export receivables. Please try again.');
    }
  };

  const handlePrint = () => {
    document.title = `Receivables-${new Date().toISOString().split('T')[0]}`;
    
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      const formatCurrencyPrint = (amount) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      const formatDatePrint = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      const totalOutstanding = filteredRows
        .filter(r => r.status.toLowerCase() !== 'paid')
        .reduce((sum, r) => sum + (r.amount || 0), 0);
      const totalPaid = filteredRows
        .filter(r => r.status.toLowerCase() === 'paid')
        .reduce((sum, r) => sum + (r.amount || 0), 0);
      const overdueAmount = filteredRows
        .filter(r => r.status.toLowerCase() === 'overdue')
        .reduce((sum, r) => sum + (r.amount || 0), 0);

      const getStatusColor = (status) => {
        const statusLower = status.toLowerCase();
        if (statusLower === 'paid') return { bg: '#e8f5e9', color: '#2e7d32', border: '#4caf50' };
        if (statusLower === 'partial') return { bg: '#fff3e0', color: '#e65100', border: '#ff9800' };
        if (statusLower === 'overdue') return { bg: '#ffebee', color: '#c62828', border: '#f44336' };
        return { bg: '#f5f5f5', color: '#616161', border: '#9e9e9e' };
      };

      const printableContent = `
        <div style="padding: 24px; backgroundColor: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
          <!-- Header -->
          <div style="padding-bottom: 24px; margin-bottom: 32px; border-bottom: 1px solid #e0e0e0;">
            <div style="font-size: 24px; font-weight: 400; letter-spacing: -0.3px; margin-bottom: 6px; color: #000;">GlowWorks</div>
            <div style="font-size: 16px; font-weight: 400; color: #424242; margin-bottom: 6px;">Accounts Receivable Report</div>
            <div style="font-size: 11px; color: #757575; font-weight: 400;">
              Generated ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
          
          <!-- Summary Cards -->
          <div style="margin-bottom: 40px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;">
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Total Outstanding
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #000;">
                ${formatCurrencyPrint(totalOutstanding)}
              </div>
            </div>
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Overdue
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #c62828;">
                ${formatCurrencyPrint(overdueAmount)}
              </div>
            </div>
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Total Paid
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #2e7d32;">
                ${formatCurrencyPrint(totalPaid)}
              </div>
            </div>
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Invoice Count
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #000;">
                ${filteredRows.length}
              </div>
            </div>
          </div>
          
          <!-- Table -->
          <div style="margin-top: 32px;">
            <div style="font-size: 13px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 16px; color: #424242;">
              Invoice Details
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
              <thead style="background: #fafafa; border-bottom: 1px solid #e0e0e0;">
                <tr>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Invoice #</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Customer</th>
                  <th style="padding: 12px; text-align: right; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Amount</th>
                  <th style="padding: 12px; text-align: center; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Status</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Due Date</th>
                  <th style="padding: 12px; text-align: right; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Days Outstanding</th>
                </tr>
              </thead>
              <tbody>
                ${filteredRows.map((receivable, index) => {
                  const status = receivable.status || 'pending';
                  const statusColors = getStatusColor(status);
                  const daysOutstanding = receivable.status.toLowerCase() === 'paid' 
                    ? 'N/A' 
                    : receivable.daysOutstanding > 0 
                      ? `+${receivable.daysOutstanding}` 
                      : receivable.daysOutstanding;
                  return `
                  <tr style="border-bottom: ${index === filteredRows.length - 1 ? 'none' : '1px solid #f5f5f5'};">
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #000;">${receivable.documentNumber || 'N/A'}</td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${receivable.customer || 'Unknown Customer'}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: right; font-weight: 400; font-variant-numeric: tabular-nums; white-space: nowrap; color: #000;">${formatCurrencyPrint(receivable.amount)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: center;">
                      <span style="display: inline-block; padding: 4px 10px; background-color: ${statusColors.bg}; color: ${statusColors.color}; border: 1px solid ${statusColors.border}; border-radius: 4px; font-size: 9px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px;">
                        ${status.toUpperCase()}
                      </span>
                    </td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${formatDatePrint(receivable.dueDate)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: right; font-weight: 400; font-variant-numeric: tabular-nums; white-space: nowrap; color: ${receivable.daysOutstanding > 0 ? '#c62828' : '#424242'};">${daysOutstanding}</td>
                  </tr>
                `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <!-- Summary Footer -->
          <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #e0e0e0;">
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; max-width: 600px; margin-left: auto;">
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Subtotal (Invoices):</span>
                <span style="font-size: 12px; font-weight: 400; color: #000;">${filteredRows.length}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Total Invoice Amount:</span>
                <span style="font-size: 12px; font-weight: 400; color: #000;">${formatCurrencyPrint(filteredRows.reduce((sum, r) => sum + (r.amount || 0), 0))}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Total Paid:</span>
                <span style="font-size: 12px; font-weight: 400; color: #2e7d32;">${formatCurrencyPrint(totalPaid)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 1px solid #e0e0e0;">
                <span style="font-size: 14px; font-weight: 400; color: #000;">Amount Outstanding:</span>
                <span style="font-size: 14px; font-weight: 400; color: #c62828;">${formatCurrencyPrint(totalOutstanding)}</span>
              </div>
            </div>
          </div>
        </div>
      `;

      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Accounts Receivable ${new Date().toLocaleDateString()}</title>
            <style>
              @page { 
                size: auto; 
                margin: 0.3in; 
              }
              @media print {
                body { 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
              body {
                margin: 0;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
                line-height: 1.3;
                letter-spacing: 0.01071em;
                background: white;
              }
            </style>
          </head>
          <body>
            ${printableContent}
          </body>
        </html>
      `;

      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        
        // Close the print window after printing
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
      
    } catch (error) {
      console.error('Print error:', error);
      setSnackbar({
        open: true,
        message: 'Error printing report. Please try again.',
        severity: 'error'
      });
    }
  };

  // Permission check - AFTER all hooks
  if (!canViewAccountingReceivables()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Receivables Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Receivables. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <HeaderCard sx={{ mb: 4 }}>
        <Paper elevation={0} sx={{ p: 4, background: 'transparent', boxShadow: 'none' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 400, color: 'primary.main', mb: 0.5 }}>
                Receivables
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem',
                mb: 1
              }}>
                {companyName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track outstanding customer invoices, monitor payment status, and manage collections. Real-time visibility into accounts receivable with automated workflow tools.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{ 
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2.5,
                    py: 1,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                    '& + .MuiToggleButton-root': {
                      marginLeft: 1.5
                    },
                    '&.Mui-selected': {
                      background: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.15)
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="table">
                  <ViewListIcon sx={{ mr: 1, fontSize: 20 }} />
                  Table
                </ToggleButton>
                <ToggleButton value="calendar">
                  <CalendarMonthIcon sx={{ mr: 1, fontSize: 20 }} />
                  Calendar
                </ToggleButton>
              </ToggleButtonGroup>
              <Button 
                variant="outlined" 
                startIcon={<ShieldIcon />} 
                onClick={() => {
                  if (!canEditAccountingReceivables()) {
                    setError('You do not have permission to access auto-collections.');
                    return;
                  }
                  setAutoCollectionsOpen(true);
                }}
                disabled={!canEditAccountingReceivables()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}
              >
                Auto-Collections
              </Button>
              <Button 
                variant="contained" 
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}
                onClick={() => {
                  if (!canEditAccountingReceivables()) {
                    setError('You do not have permission to create invoices.');
                    return;
                  }
                  window.open(`/company/${companyId}/invoices/new`, '_blank');
                }}
                disabled={!canEditAccountingReceivables()}
              >
                New Invoice
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </HeaderCard>

      {/* Summary Cards */}
      <ReceivablesSummary summaryStats={summaryStats} loading={loading} />

      <SectionPaper sx={{ mb: 4, mt: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search by customer or invoice #"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' }
                  }
                }}
              >
                {filters.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                type="number"
                label="Due in Days"
                value={dueInDays}
                onChange={(e) => setDueInDays(e.target.value)}
                placeholder="All"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={1.75}>
              <DatePicker
                label="From Date"
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(newValue) => setFromDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                maxDate={toDate ? dayjs(toDate) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={1.75}>
              <DatePicker
                label="To Date"
                value={toDate ? dayjs(toDate) : null}
                onChange={(newValue) => setToDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                minDate={fromDate ? dayjs(fromDate) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Chip 
                label={`DSO ${metrics.dso} days`} 
                color={metrics.dso <= 30 ? 'success' : metrics.dso <= 45 ? 'warning' : 'error'} 
                variant="outlined" 
              />
              <Chip 
                label={`${metrics.totalInvoices} invoices | ${formatCurrency(metrics.totalAmount)}`} 
                color="primary" 
                variant="outlined" 
              />
              {metrics.highRisk > 0 && (
                <Chip 
                  label={`${metrics.highRisk} high-risk`} 
                  color="warning" 
                  variant="outlined" 
                />
              )}
            </Stack>
          </Grid>
        </Grid>
        </LocalizationProvider>
      </SectionPaper>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={filteredRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Export
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={filteredRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Print
            </Button>
            {selectedReceivables.length > 0 && (
              <>
                <Button 
                  size="small" 
                  variant="contained" 
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleBulkMarkAsPaid}
                  disabled={!canEditAccountingReceivables()}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Mark {selectedReceivables.length} as Paid
                </Button>
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleBulkMarkAsUnpaid}
                  disabled={!canEditAccountingReceivables()}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Mark {selectedReceivables.length} as Unpaid
                </Button>
              </>
            )}
          </Stack>
        </Stack>
        {loading ? (
          <Card sx={{ 
            borderRadius: 3,
            mt: 10,
            mx: 'auto',
            maxWidth: 600,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
            }
          }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'inline-flex',
                  p: 3,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mb: 2
                }}>
                  <ReceiptLongIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                mb: 1,
                fontSize: '1.1rem'
              }}>
                Loading Receivables
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                maxWidth: 400,
                mx: 'auto',
                lineHeight: 1.6,
                mb: 3
              }}>
                Analyzing invoice data and payment status...
              </Typography>
              <Box sx={{ 
                width: '60%', 
                mx: 'auto',
                '& .MuiLinearProgress-root': {
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(theme.palette.grey[300], 0.3),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'primary.main',
                    borderRadius: 4
                  }
                }
              }}>
                <LinearProgress />
              </Box>
            </CardContent>
          </Card>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No receivables found
            </Typography>
          </Box>
        ) : viewMode === 'table' ? (
          <Paper sx={{ 
            borderRadius: 3, 
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      padding="checkbox"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5,
                        width: 48
                      }}
                    >
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Document
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Customer
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Due Date
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Days Outstanding
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow 
                      key={`${row.type}-${row.id}`} 
                      hover
                      selected={selectedReceivables.includes(row.id)}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          transition: 'all 0.2s ease',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          px: 2.5
                        }
                      }}
                    >
                      <TableCell 
                        padding="checkbox"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectReceivable(row.id);
                        }}
                      >
                        <Checkbox
                          checked={selectedReceivables.includes(row.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectReceivable(row.id);
                          }}
                          color="primary"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                          INV: {row.documentNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Invoice
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                          {row.customer}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(row.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={
                            row.status.toLowerCase() === 'overdue'
                              ? 'error'
                              : row.status.toLowerCase() === 'pending'
                                ? 'warning'
                                : row.status.toLowerCase() === 'paid'
                                  ? 'success'
                                  : row.status.toLowerCase() === 'partial'
                                    ? 'info'
                                    : 'default'
                          }
                          variant="outlined"
                          sx={{ borderRadius: 2, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(row.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.status.toLowerCase() === 'paid' ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            N/A
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: row.daysOutstanding > 0 ? theme.palette.error.main : 'text.secondary',
                              fontWeight: row.daysOutstanding > 0 ? 500 : 400
                            }}
                          >
                            {row.daysOutstanding > 0 ? `+${row.daysOutstanding}` : row.daysOutstanding} days
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button 
                          size="small" 
                          sx={{ textTransform: 'none' }}
                          onClick={(e) => handleViewWorkflowClick(e, row)}
                          disabled={!canEditAccountingReceivables()}
                        >
                          View Workflow
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Box sx={{ mb: 3, mt: 5 }}>
            <ReceivablesCalendar
              receivables={filteredRows}
              onViewDetails={(receivable) => {
                setSelectedReceivable(receivable);
                setWorkflowMenuAnchor(null);
                // Open the workflow menu or navigate to invoice
                if (receivable.proformaInvoiceId) {
                  window.open(`/company/${companyId}/proforma-invoices/${receivable.proformaInvoiceId}`, '_blank');
                } else {
                  window.open(`/company/${companyId}/invoices/edit/${receivable.id}`, '_blank');
                }
              }}
              canEdit={canEditAccountingReceivables()}
            />
          </Box>
        )}
      </Box>

      {/* Workflow Menu */}
      <Menu
        anchorEl={workflowMenuAnchor}
        open={Boolean(workflowMenuAnchor)}
        onClose={handleWorkflowMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedReceivable?.proformaInvoiceId && (
          <MenuItem onClick={handleViewOrderWorkflow}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Order Workflow</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleViewInvoiceWorkflow}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Invoice Workflow</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleMarkAsPaid}
          disabled={selectedReceivable?.status.toLowerCase() === 'paid'}
        >
          <ListItemIcon>
            <CheckCircleIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Mark as Paid</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleMarkAsPending}
          disabled={selectedReceivable?.status.toLowerCase() === 'pending'}
        >
          <ListItemIcon>
            <PendingIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Mark as Pending</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleMarkAsUnpaid}
          disabled={selectedReceivable?.status.toLowerCase() === 'pending' || selectedReceivable?.status.toLowerCase() === 'overdue'}
        >
          <ListItemIcon>
            <CancelIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Mark as Unpaid (Reset to Pending)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Auto-Collections Dialog */}
      <AutoCollectionsDialog
        open={autoCollectionsOpen}
        onClose={() => setAutoCollectionsOpen(false)}
        receivables={receivables}
        companyId={companyId}
        canEdit={canEditAccountingReceivables()}
        canDelete={canDeleteAccountingReceivables()}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountingReceivablesPage;